import {BaseComponent} from '../base/component.base.js';
import {
    moneyInputValue,
    periodText,
    proratedAmount,
    toLocalIsoDate,
    validateMarkInvoicedForm,
} from '../../core/billing/billing.core.js';
import template from './mark-invoiced-dialog.template.html';
import dialogStyles from '../base/form-dialog.styles.css';
import styles from './mark-invoiced-dialog.styles.css';

const FIELDS = Object.freeze({
    invoiceNumber: '#invoice-number',
    invoicedOn: '#invoiced-on',
    periodTo: '#period-to',
    amount: '#amount',
    cost: '#cost',
});

/**
 * Dialog that records an invoice for one billing item. Independent of any page: whoever holds an
 * item calls `open(item)`. Submitting a valid form emits `billing-invoice-submit` with
 * `{id, request}`; the driving adapter sends it and then calls `close()` or `showError()`.
 */
export class MarkInvoicedDialogComponent extends BaseComponent {
    /** Gives "today"; replaceable so tests do not depend on the clock. @type {() => Date} */
    today = () => new Date();
    /** @type {import('../../shell/types/billing.types.js').BillingItemApiResponse | null} */
    #item = null;
    #submitting = false;
    /** Fields the owner typed in since `open`; the period end no longer prefills them. */
    #edited = {amount: false, cost: false};

    connectedCallback() {
        this.loadTemplate(template, dialogStyles + styles);
        this.$('#form').addEventListener('submit', (e) => this.#handleSubmit(e));
        this.$('#cancel').addEventListener('click', () => this.close());
        // Escape must not close the dialog under an in-flight request: the late answer would land on
        // whichever item the dialog shows by then.
        this.$('#dialog').addEventListener('cancel', (e) => {
            if (this.#submitting) e.preventDefault();
        });
        this.$('#period-to').addEventListener('input', () => {
            this.#updatePeriodText();
            this.#prefillProrated();
        });
        for (const name of Object.keys(this.#edited)) {
            this.$(FIELDS[name]).addEventListener('input', () => {
                this.#edited[name] = true;
            });
        }
    }

    /**
     * Opens the dialog for an item with the defaults filled in: invoice date today, period end the
     * item's next period end, amount its price and supplier cost its cost.
     *
     * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse} item
     */
    open(item) {
        this.#item = item;
        this.$('#dialog-item').textContent = `${item.clientName}: ${item.label}`;
        this.$('#invoice-number').value = '';
        this.$('#invoiced-on').value = toLocalIsoDate(this.today());
        this.$('#period-to').value = item.nextPeriodTo;
        this.$('#amount').value = moneyInputValue(item.price);
        this.$('#cost').value = moneyInputValue(item.cost);
        this.#edited = {amount: false, cost: false};
        this.#showFieldErrors({});
        this.showError(null);
        this.submitting = false;
        this.#updatePeriodText();
        this.$('#dialog').showModal();
        this.$('#invoice-number').focus();
    }

    close() {
        this.$('#dialog').close();
        this.#item = null;
    }

    /**
     * Shows why the invoice was rejected inside the dialog, since the modal dialog covers the page
     * and anything shown behind it.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} whether the dialog is open, so the message is on screen
     */
    showError(message) {
        const error = this.$('#dialog-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.$('#dialog').open;
    }

    /** @param {boolean} value disables the buttons while a request runs, so it cannot be sent twice */
    set submitting(value) {
        this.#submitting = value;
        this.$('#submit').disabled = value;
        this.$('#cancel').disabled = value;
    }

    #updatePeriodText() {
        this.$('#period-text').textContent = periodText(this.#item.paidUntil, this.$('#period-to').value);
    }

    /**
     * Prefills the amount and the cost the owner has not typed in with the prorated value; a period
     * end that gives none (still being typed, not after paid until) leaves the field as it is.
     */
    #prefillProrated() {
        const item = this.#item;
        const periodTo = this.$('#period-to').value;
        const values = {amount: item.price, cost: item.cost};
        for (const [name, value] of Object.entries(values)) {
            if (this.#edited[name]) continue;
            const prorated = proratedAmount(value, item.paidUntil, item.nextPeriodTo, periodTo);
            if (prorated !== null) {
                this.$(FIELDS[name]).value = moneyInputValue(prorated);
            }
        }
    }

    #showFieldErrors(errors) {
        for (const [name, selector] of Object.entries(FIELDS)) {
            const message = errors[name] ?? '';
            const error = this.$(`#error-${name}`);
            error.textContent = message;
            error.hidden = !message;
            this.$(selector).setAttribute('aria-invalid', String(Boolean(message)));
        }
    }

    #handleSubmit(e) {
        e.preventDefault();
        this.showError(null);
        const form = {};
        for (const [name, selector] of Object.entries(FIELDS)) {
            form[name] = this.$(selector).value;
        }
        const {valid, errors, request} = validateMarkInvoicedForm(form, this.#item.paidUntil);
        this.#showFieldErrors(errors);
        if (!valid) {
            const first = Object.keys(FIELDS).find((name) => errors[name]);
            this.$(FIELDS[first]).focus();
            return;
        }
        this.emit('billing-invoice-submit', {id: this.#item.id, request});
    }
}

customElements.define('mark-invoiced-dialog', MarkInvoicedDialogComponent);
